<template>
  <div class="container-custom moo-sheet-page moo-reward-page" :style="styleObject">
    <div class="content-custom">
      <div class="moo-sheet-shell">
        <MooSheetHead
          icon="redeem"
          :title="$t('rewardCatalogue.title')"
          @close="goBack()"
        />

        <div v-if="isLoadDone">
          <!-- Guest: the catalogue is member-only, same as before the reskin -->
          <div class="rc-guest" v-if="!isLogined">
            <span class="material-icons-outlined">card_giftcard</span>
            <p>
              <span class="rc-link" @click="gotoSignIn()">{{
                $t('rewardCatalogue.logIn')
              }}</span>
              {{ $t('rewardCatalogue.orWord') }}
              <span class="rc-link" @click="gotoSignUp()">{{
                $t('rewardCatalogue.signUp') }}</span>
            </p>
          </div>

          <div class="rc-empty" v-else-if="notAvailable">
            <span class="material-icons-outlined">block</span>
            <span>{{ $t('rewardCatalogue.notAvailable') }}</span>
          </div>

          <template v-else>
            <div class="sheet-balances rc-balances">
              <div class="sheet-balance">
                <div class="sheet-balance__info">
                  <div class="sheet-balance__label">
                    {{ $t('rewardCatalogue.walletBalance') }}
                  </div>
                  <div class="sheet-balance__amount">
                    {{ merchantMember.amount_format }}
                  </div>
                </div>
                <span class="material-icons-round sheet-balance__icon"
                  >account_balance_wallet</span
                >
              </div>
              <div class="sheet-balance" v-if="isPointMerchant">
                <div class="sheet-balance__info">
                  <div class="sheet-balance__label">{{ pointName }}</div>
                  <div class="sheet-balance__amount">
                    {{ merchantMember.point_format }}
                  </div>
                </div>
                <!-- The merchant's own icon when they uploaded one; the Material
                     glyph otherwise. point_icon alone cannot decide this — the API
                     fills it with a default asset either way. -->
                <img
                  v-if="hasCustomPointIcon"
                  class="sheet-balance__icon sheet-balance__icon--img"
                  :src="pointIcon"
                  :alt="pointName"
                />
                <span v-else class="material-icons-round sheet-balance__icon"
                  >stars</span
                >
              </div>
            </div>

            <SearchInput
              class="rc-search"
              :placeholder="$t('rewardCatalogue.searchPlaceholder')"
              :onChangesearchKeyword="onChangesearchKeyword"
            />

            <!-- Options come from what actually loaded, so a merchant never
                 gets a dropdown listing a kind it does not sell. A chip is
                 hidden only when it would have nothing at all to offer. -->
            <div class="rc-filters" v-if="typeOptions.length || kindOptions.length">
              <div class="rc-select" v-if="typeOptions.length" ref="typeSelect">
                <button
                  type="button"
                  class="rc-filter"
                  :class="{ 'rc-filter--open': openMenu === 'type' }"
                  @click="toggleMenu('type')"
                >
                  <span class="rc-filter__left">
                    <span class="material-icons-outlined rc-filter__icon">sell</span>
                    <span class="rc-filter__text">{{ typeLabel }}</span>
                  </span>
                  <span
                    class="material-icons-round rc-filter__chevron"
                    :class="{ 'rc-filter__chevron--open': openMenu === 'type' }"
                    >expand_more</span
                  >
                </button>
                <div class="rc-select__menu" v-if="openMenu === 'type'">
                  <button
                    v-for="option in typeOptions"
                    :key="option.value"
                    type="button"
                    class="rc-select__opt"
                    :class="{ 'rc-select__opt--active': selectedTypes.includes(option.value) }"
                    @click="toggleType(option.value)"
                  >
                    <span class="rc-select__opt-label">{{ option.label }}</span>
                    <span
                      class="material-icons-round rc-select__check"
                      v-if="selectedTypes.includes(option.value)"
                      >check</span
                    >
                  </button>
                </div>
              </div>

              <div class="rc-select" v-if="kindOptions.length" ref="kindSelect">
                <button
                  type="button"
                  class="rc-filter"
                  :class="{ 'rc-filter--open': openMenu === 'kind' }"
                  @click="toggleMenu('kind')"
                >
                  <span class="rc-filter__left">
                    <span class="material-icons-outlined rc-filter__icon">inventory_2</span>
                    <span class="rc-filter__text">{{ kindLabel }}</span>
                  </span>
                  <span
                    class="material-icons-round rc-filter__chevron"
                    :class="{ 'rc-filter__chevron--open': openMenu === 'kind' }"
                    >expand_more</span
                  >
                </button>
                <div class="rc-select__menu" v-if="openMenu === 'kind'">
                  <button
                    v-for="option in kindOptions"
                    :key="option.value"
                    type="button"
                    class="rc-select__opt"
                    :class="{ 'rc-select__opt--active': selectedKinds.includes(option.value) }"
                    @click="toggleKind(option.value)"
                  >
                    <span class="rc-select__opt-label">{{ option.label }}</span>
                    <span
                      class="material-icons-round rc-select__check"
                      v-if="selectedKinds.includes(option.value)"
                      >check</span
                    >
                  </button>
                </div>
              </div>
            </div>

            <div class="rc-list" v-if="visibleVouchers.length">
              <div
                class="rc-card"
                :class="{ 'rc-card--ineligible': !canRedeem(item) }"
                v-for="(item, index) in visibleVouchers"
                :key="item.kind + '_' + item.id + '_' + index"
              >
                <div class="rc-card__icon" @click="goDetail(item)">
                  <img v-if="item.icon_url" :src="item.icon_url" :alt="item.reward_title" />
                  <span class="material-icons-outlined" v-else>redeem</span>
                </div>

                <div class="rc-card__body">
                  <span class="rc-badge" :class="'rc-badge--' + item.kind">
                    {{ kindLabelOf(item.kind) }}
                  </span>
                  <div class="rc-card__name" @click="goDetail(item)">
                    {{ item.reward_title || "" }}
                  </div>
                  <div class="rc-card__valid" v-if="item.expiry_date_text">
                    {{ $t('rewardCatalogue.validFor', { date: item.expiry_date_text }) }}
                  </div>
                  <div class="rc-card__count" v-if="item.number_of_vouchers_text">
                    {{ item.number_of_vouchers_text }}
                  </div>

                  <div class="rc-card__foot">
                    <span class="rc-cost">{{ costText(item) }}</span>
                    <button
                      type="button"
                      class="rc-get"
                      :class="{ 'rc-get--ineligible': !canRedeem(item) }"
                      :disabled="!canRedeem(item)"
                      @click="openRedeemConfirm(item)"
                    >
                      {{ $t('rewardCatalogue.redeem') }}
                    </button>
                  </div>

                  <div class="rc-tnc" v-if="item.terms_conditions">
                    <span @click.stop="openTermAndConditions(item.terms_conditions)">
                      {{ $t('rewardCatalogue.viewTnc') }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="rc-empty" v-else>
              <span class="material-icons-outlined">card_giftcard</span>
              <span>{{ hasAnyVoucher ? $t('rewardCatalogue.noMatch') : $t('rewardCatalogue.noRewards') }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>

    <!-- Terms & conditions -->
    <button
      style="display: none"
      type="button"
      class="btn-term-and-conditions-modal"
      data-bs-toggle="modal"
      data-bs-target="#modalShowTermAndConditions"
    ></button>
    <div
      class="modal fade moo-bottom-sheet"
      id="modalShowTermAndConditions"
      tabindex="-1"
      aria-labelledby="modalShowTermAndConditionsLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content rc-terms">
          <div class="rc-terms__head">
            <span class="material-icons-round rc-terms__icon">gavel</span>
            <div class="rc-terms__title" id="modalShowTermAndConditionsLabel">
              {{ $t('rewardCatalogue.termsTitle') }}
            </div>
            <button
              type="button"
              class="rc-terms__close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span class="material-icons-round">close</span>
            </button>
          </div>
          <div class="rc-terms__body" v-html="contentTermAndCondition"></div>
          <button type="button" class="rc-terms__done" data-bs-dismiss="modal">
            {{ $t('common.close') }}
          </button>
        </div>
      </div>
    </div>

    <ModalConfirmSheet
      :message="confirmMessage"
      icon="redeem"
      :confirm-text="$t('common.yes')"
      :cancel-text="$t('common.cancel')"
      :process-yes="processExchange"
    >
      <!-- Only bundles can be paid by card, so a single voucher shows no
           picker at all rather than a one-option radio. -->
      <div class="sheet-methods" v-if="showPayPicker">
        <button
          type="button"
          class="sheet-method"
          :class="{
            'sheet-method--selected': pickedPay === PAY_BALANCE,
            'sheet-method--disabled': selectedRedeem && selectedRedeem.is_disabled,
          }"
          :disabled="selectedRedeem && selectedRedeem.is_disabled"
          @click="pickedPay = PAY_BALANCE"
        >
          <span class="sheet-method__radio"></span>
          <span class="material-icons-round sheet-method__icon">{{
            isPointMerchant ? "stars" : "account_balance_wallet"
          }}</span>
          <span class="sheet-method__body">
            <span class="sheet-method__label">{{ balanceMethodLabel }}</span>
            <span class="sheet-method__note">
              {{ balanceText }}
              <template v-if="selectedRedeem && selectedRedeem.is_disabled">
                — {{ $t('rewardCatalogue.notEnough') }}
              </template>
            </span>
          </span>
        </button>

        <button
          type="button"
          class="sheet-method"
          :class="{
            'sheet-method--selected': pickedPay === PAY_CARD,
            'sheet-method--disabled': selectedRedeem && selectedRedeem.is_disabled_by_card,
          }"
          :disabled="selectedRedeem && selectedRedeem.is_disabled_by_card"
          @click="pickedPay = PAY_CARD"
        >
          <span class="sheet-method__radio"></span>
          <span class="material-icons-round sheet-method__icon">credit_card</span>
          <span class="sheet-method__body">
            <span class="sheet-method__label">{{ $t('rewardCatalogue.payCard') }}</span>
            <span class="sheet-method__note">{{ $t('rewardCatalogue.payCardNote') }}</span>
          </span>
        </button>
      </div>
    </ModalConfirmSheet>

    <!-- Redeem success: the shared sheet the two detail screens report with,
         so a voucher and a bundle are announced the same way wherever they were
         redeemed from. -->
    <ModalSuccess
      :processAcction="gotoMyVouchers"
      :success="true"
      :message="redeemSuccessMessage"
      :buttonText="$t('rewardCatalogue.goToMyVouchers')"
    />

    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import SearchInput from "@/components/SearchInput.vue";
import MooSheetHead from "@/components/MooSheetHead.vue";
import ModalConfirmSheet from "@/components/ModalConfirmSheet.vue";
import ModalSuccess from "@/components/ModalSuccess.vue";
import PromocodeService from "../services/promocode.service";
import MemberService from "../services/member.service";
import { STRIPE, AIRWALLEX } from "@/config/const";
import {
  gotoMemberLogin,
  localStorageName,
  stashPendingRedeem,
  takePendingRedeem,
  isGatewayReturn,
} from "@/config/utils";
import $ from "jquery";

// MintyMerReward::TYPE_* — the catalogue endpoint returns these on each single
// voucher. Bundles carry no reward_type, so a type filter never matches them.
const TYPE_PERCENTAGE_OFF = 1;
const TYPE_FIXED_AMOUNT_OFF = 2;
const TYPE_FREE_ITEM = 3;

const KIND_SINGLE = "single";
const KIND_BUNDLE = "bundle";

// Redeem can draw on the member's balance or, for bundles only, a card.
const PAY_BALANCE = "balance";
const PAY_CARD = "card";

// merchant_setting.reward_per_transaction value meaning "points, not cashback".
const REWARD_POINT = 2;

const SEARCH_DEBOUNCE_MS = 1000;

export default {
  name: "reward-catalogue",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    SearchInput,
    MooSheetHead,
    ModalConfirmSheet,
    ModalSuccess,
  },
  data() {
    return {
      isLoadDone: false,
      isLogined: false,
      notAvailable: false,
      singleVouchers: [],
      bundleVouchers: [],
      merchantMember: {},
      // Both list endpoints return the member row; the card checkout needs its
      // id, and merchant_member is a different table.
      currentMember: {},
      pointName: "Point",
      pointIcon: "",
      hasCustomPointIcon: false,
      searchKeyword: "",
      searchTimerId: null,
      selectedTypes: [],
      selectedKinds: [],
      openMenu: null,
      contentTermAndCondition: "",
      selectedRedeem: null,
      // Set only on the way back from a card payment, where selectedRedeem is
      // gone. Same shape redeemedSummary() produces.
      pendingRedeem: null,
      // The issuing job had not reported back by the time the success sheet
      // opened; the receipt says so rather than promising a finished voucher.
      redeemStillProcessing: false,
      // Read before handleCardReturn() strips the query; goBack() needs it.
      cameFromGateway: false,
      PAY_BALANCE,
      PAY_CARD,
      pickedPay: PAY_BALANCE,
      paymentMode: STRIPE,
      loaderActive: false,
      snackbar: null,
      message: "",
    };
  },
  created() {
    const memberInfoData = JSON.parse(
      localStorage.getItem(localStorageName("memberInfo")) || "null"
    );
    this.isLogined = !!memberInfoData;
    if (!this.isLogined) {
      this.isLoadDone = true;
    }
  },
  async mounted() {
    this.cameFromGateway = isGatewayReturn(this.$route.query);
    document.addEventListener("click", this.closeMenuOnOutsideClick);
    const successModal = document.getElementById("success-modal");
    if (successModal) {
      successModal.addEventListener("hidden.bs.modal", this.refreshOnSuccessClose);
    }

    if (!this.isLogined) {
      return;
    }
    this.getDefaultPaymentMode();
    // Verify BEFORE listing, the order Store.vue uses: the check call is what
    // dispatches ProcessWebhookJob, so running it first gives the queue a head
    // start and the one list fetch below stands a chance of already reflecting
    // the redeemed voucher. Loading here rather than in created() also means
    // the DOM exists when the success sheet is opened.
    await this.handleCardReturn();
    await this.getList();
    this.isLoadDone = true;
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeMenuOnOutsideClick);
    const successModal = document.getElementById("success-modal");
    if (successModal) {
      successModal.removeEventListener("hidden.bs.modal", this.refreshOnSuccessClose);
    }
    clearTimeout(this.searchTimerId);
  },
  computed: {
    styleObject() {
      // Same Theme 2 base the rest of the reskinned MOO screens use.
      return {
        "--color":
          this.$store.state.mainThemeColorTheme2 ||
          this.$store.state.color ||
          "#201a1a",
      };
    },
    isPointMerchant() {
      return this.merchantMember.reward_type == REWARD_POINT;
    },
    // One list out of the two endpoints. `kind` is added here — the API has no
    // such field, the two payloads are simply shaped the same.
    allVouchers() {
      return [
        ...this.singleVouchers.map((item) => ({ ...item, kind: KIND_SINGLE })),
        ...this.bundleVouchers.map((item) => ({ ...item, kind: KIND_BUNDLE })),
      ];
    },
    hasAnyVoucher() {
      return this.allVouchers.length > 0;
    },
    typeOptions() {
      const names = {
        [TYPE_PERCENTAGE_OFF]: this.$t("rewardCatalogue.typePercentage"),
        [TYPE_FIXED_AMOUNT_OFF]: this.$t("rewardCatalogue.typeFixed"),
        [TYPE_FREE_ITEM]: this.$t("rewardCatalogue.typeFreeItem"),
      };
      const present = [
        ...new Set(this.singleVouchers.map((item) => item.reward_type)),
      ].filter((value) => names[value]);
      return present.map((value) => ({ value: value, label: names[value] }));
    },
    // Both kinds are always offered, even when the merchant currently has none
    // of one: picking it and getting "no rewards match" tells the member that
    // much, whereas a missing option is indistinguishable from a broken filter.
    kindOptions() {
      return [
        { value: KIND_SINGLE, label: this.$t("rewardCatalogue.singleVoucher") },
        { value: KIND_BUNDLE, label: this.$t("rewardCatalogue.bundleVoucher") },
      ];
    },
    balanceText() {
      return this.isPointMerchant
        ? this.merchantMember.point_format
        : this.merchantMember.amount_format;
    },
    balanceMethodLabel() {
      return this.isPointMerchant
        ? this.$t("rewardCatalogue.payPoint", { name: this.pointName })
        : this.$t("rewardCatalogue.payWallet");
    },
    // Both kinds can be bought by card: Exchange.vue's own checkout branches on
    // isExchangeVoucherBundle vs isExchangeVoucher, and the BE computes
    // is_disabled_by_card for singles too. Only its radio was gated to bundles.
    showPayPicker() {
      const item = this.selectedRedeem;
      return !!item && !item.is_disabled_by_card;
    },
    // Receipt body for the confirm sheet, same ps-* layout the top-up and
    // membership sheets use. Every figure is a string the API already
    // formatted — nothing is recomputed here, so the currency/point format
    // cannot drift from the rest of the app.
    confirmMessage() {
      const item = this.selectedRedeem;
      if (!item) {
        return "";
      }
      const rows = [
        {
          icon: "sell",
          label: this.$t("rewardCatalogue.rowType"),
          value: this.kindLabelOf(item.kind),
        },
        {
          icon: this.isPointMerchant ? "stars" : "payments",
          label: this.$t("rewardCatalogue.rowCost"),
          value: this.costText(item),
        },
        {
          icon: "account_balance_wallet",
          label: this.$t("rewardCatalogue.rowBalance"),
          value: this.isPointMerchant
            ? this.merchantMember.point_format
            : this.merchantMember.amount_format,
        },
      ];

      const rowsHtml = rows
        .map(
          (r) => `
        <div class="ps-row">
          <span class="material-icons-outlined ps-row-icon">${r.icon}</span>
          <span class="ps-row-label">${r.label}</span>
          <span class="ps-row-value">${r.value ?? ""}</span>
        </div>`
        )
        .join("");

      return `
        <span class="ps-title">${this.$t("rewardCatalogue.confirmTitle")}</span>
        <span class="ps-amount">${item.reward_title || ""}</span>
        <span class="ps-rows">${rowsHtml}</span>
      `;
    },
    // Result receipt, deliberately the same shape as confirmMessage above: the
    // member confirms on one sheet and is reported to on the next, so they read
    // as one action rather than two unrelated dialogs.
    //
    // No balance row here, unlike the confirm sheet. merchantMember is only
    // refetched when this sheet closes, so a balance printed now would be the
    // pre-redemption figure — the same staleness that made TopUp's receipt wrong.
    // Whichever path got here: the item still in hand after a balance redeem,
    // or the title payByCard stashed before the redirect — the component is
    // rebuilt on the way back from the gateway and keeps nothing else.
    redeemedTitle() {
      const item = this.selectedRedeem;

      if (item) {
        return item.reward_title || "";
      }

      return this.pendingRedeem?.title ?? "";
    },
    redeemSuccessMessage() {
      const note = this.redeemStillProcessing
        ? `<span class="ps-note">${this.$t(
            "rewardCatalogue.redeemStillProcessing"
          )}</span>`
        : "";

      return `
        <span class="ps-title">${this.$t("rewardCatalogue.redeemSuccess")}</span>
        <span class="ps-amount">${this.redeemedTitle}</span>
        ${note}
      `;
    },
    typeLabel() {
      return this.filterLabel(
        this.selectedTypes,
        this.typeOptions,
        this.$t("rewardCatalogue.allRewardTypes")
      );
    },
    kindLabel() {
      return this.filterLabel(
        this.selectedKinds,
        this.kindOptions,
        this.$t("rewardCatalogue.allVoucherTypes")
      );
    },
    visibleVouchers() {
      return this.allVouchers.filter((item) => {
        // A selected reward type excludes bundles, which have none.
        if (
          this.selectedTypes.length &&
          !this.selectedTypes.includes(item.reward_type)
        ) {
          return false;
        }
        if (this.selectedKinds.length && !this.selectedKinds.includes(item.kind)) {
          return false;
        }
        return true;
      });
    },
  },
  methods: {
    filterLabel(selected, options, allLabel) {
      if (!selected.length) {
        return allLabel;
      }
      if (selected.length === 1) {
        const match = options.find((option) => option.value === selected[0]);
        return match ? match.label : allLabel;
      }
      return this.$t("rewardCatalogue.typesSelected", { count: selected.length });
    },
    kindLabelOf(kind) {
      return kind === KIND_BUNDLE
        ? this.$t("rewardCatalogue.bundleVoucher")
        : this.$t("rewardCatalogue.singleVoucher");
    },
    toggleMenu(name) {
      this.openMenu = this.openMenu === name ? null : name;
    },
    closeMenuOnOutsideClick(event) {
      if (!this.openMenu) {
        return;
      }
      const root = this.$refs[this.openMenu + "Select"];
      if (root && !root.contains(event.target)) {
        this.openMenu = null;
      }
    },
    toggleType(value) {
      const index = this.selectedTypes.indexOf(value);
      if (index > -1) {
        this.selectedTypes.splice(index, 1);
      } else {
        this.selectedTypes.push(value);
      }
    },
    toggleKind(value) {
      const index = this.selectedKinds.indexOf(value);
      if (index > -1) {
        this.selectedKinds.splice(index, 1);
      } else {
        this.selectedKinds.push(value);
      }
    },
    // Redeemable if at least one method is open — a bundle out of reach on
    // balance can still be bought by card.
    canRedeem(item) {
      return !item.is_disabled || !item.is_disabled_by_card;
    },
    // Redeem cost: points or cash, depending on how the merchant rewards.
    costText(item) {
      return this.isPointMerchant ? item.point_text : item.amount_text;
    },
    onChangesearchKeyword(event) {
      this.searchKeyword = event.target.value;
      clearTimeout(this.searchTimerId);
      this.searchTimerId = setTimeout(() => {
        this.getList();
      }, SEARCH_DEBOUNCE_MS);
    },
    openTermAndConditions(content) {
      this.contentTermAndCondition = content;
      $(".btn-term-and-conditions-modal").click();
    },
    openRedeemConfirm(item) {
      this.selectedRedeem = item;
      // Preselect whichever method is actually usable.
      this.pickedPay = item.is_disabled ? PAY_CARD : PAY_BALANCE;
      $(".btn-confirm-sheet-modal").click();
    },
    goBack() {
      // Landed here from a gateway redirect: the entry behind this one is the
      // hosted checkout page, so go(-1) would take the member back OUT of the
      // app. MyReward is where this screen is opened from.
      if (this.cameFromGateway) {
        this.$router.push({ name: "MyReward", params: this.$route.params });
        return;
      }
      this.$router.go(-1);
    },
    gotoSignIn() {
      gotoMemberLogin("order", false, "login");
    },
    gotoSignUp() {
      gotoMemberLogin("order", false, "signup");
    },
    // The list of vouchers the member now HOLDS, not MyReward — that screen
    // picks a voucher to apply to the order in hand, so a member who just
    // redeemed one landed on a picker instead of on what they had just bought.
    // Same destination the member site sends them to.
    gotoMyVouchers() {
      // Also the success sheet's action button, which has no data-bs-dismiss of
      // its own — same reason RewardDetail.vue dismisses by hand before leaving.
      $("#success-modal .btn-close").click();
      this.$router.push({ name: "MyVouchers", params: this.$route.params });
    },
    goDetail(item) {
      // The two kinds have separate detail screens.
      if (item.kind === KIND_BUNDLE) {
        this.$router.push({
          name: "VoucherBundleDetail",
          params: { ...this.$route.params, id: item.id },
        });
        return;
      }
      this.$router.push({
        name: "RewardDetail",
        params: { ...this.$route.params, reward_id: item.id },
      });
    },
    async processExchange() {
      // Both sheet buttons carry data-bs-dismiss, so it is already closing.
      const item = this.selectedRedeem;
      if (!item || !item.id) {
        return;
      }
      if (this.pickedPay === PAY_CARD) {
        this.payByCard(item);
        return;
      }
      this.closeSnackbar();
      this.showLoader();

      const request =
        item.kind === KIND_BUNDLE
          ? PromocodeService.exchangeVoucherBundle({ reward_id: item.id })
          : PromocodeService.exchangeReward({ reward_id: item.id });

      await request.then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.getList();
            // Granted inline, no queued job to wait on.
            this.redeemStillProcessing = false;
            $(".btn-success-modal").click();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error;
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    // Both kinds are issued server-side by the webhook, off
    // metadata.isExchangeVoucherBundle or metadata.isExchangeVoucher. The
    // return URL confirms nothing itself; it only lands back here so the
    // outcome can be reported.
    payByCard(item) {
      this.closeSnackbar();
      this.showLoader();

      const merchantCode = this.$store.state.merchantCode;
      let url = location.protocol + "//" + location.host + "/" + merchantCode;
      if (this.$route.params.table_code) {
        url +=
          "/QR/" +
          this.$store.state.branchCode +
          "/" +
          this.$route.params.table_code +
          "/reward-catalogue";
      } else {
        url += "/" + this.$store.state.branchCode + "/reward-catalogue";
      }

      // The sheet on the way back names the voucher; nothing else survives the trip.
      stashPendingRedeem({ title: item.reward_title || "" });

      const isBundle = item.kind === KIND_BUNDLE;
      const dataSend = {
        images: item.icon_url ?? "",
        name: item.reward_title || "",
        unit_amount: item.amount_convert,
        quantity: 1,
        member_id: this.currentMember.id,
        voucher_id: item.id,
        // Marked so the landing page can tell "came back from the gateway"
        // from a normal visit — cancelling leaves the checkout page sitting in
        // history behind us exactly the way paying does.
        cancel_url: url + "?exchange=cancel",
      };
      if (isBundle) {
        dataSend.isExchangeVoucherBundle = 1;
      } else {
        dataSend.isExchangeVoucher = 1;
      }

      const isAirwallex = this.paymentMode == AIRWALLEX;
      const request = isAirwallex
        ? MemberService.checkoutAirwallex({
            ...dataSend,
            // The BE appends airwallex_intent_id to this URL itself.
            success_url: url + "?exchange=success",
          })
        : MemberService.checkoutStripe({
            ...dataSend,
            success_url:
              url + "?session_id={CHECKOUT_SESSION_ID}&exchange=success",
          });

      request.then(
        (response) => {
          if (response.data.result.isSuccess) {
            window.location.href = response.data.data.url;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
            this.hideLoader();
          }
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? "Checkout failed";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    async handleCardReturn() {
      const query = this.$route.query;
      if (query.exchange !== "success") {
        // Cancelled at the gateway. cameFromGateway has already been read, so
        // the marker has done its job and can go.
        if (query.exchange) {
          this.$router.replace({ path: this.$route.path, query: {} });
        }
        return;
      }

      if (!query.session_id && !query.airwallex_intent_id) {
        return;
      }

      // Consumed here whatever the outcome: a stash left behind by a payment
      // that failed would otherwise be attached to the next redeem's sheet.
      const stashed = takePendingRedeem();

      this.showLoader();

      // WAIT for the job that actually issues the voucher, don't just verify
      // the payment. The check endpoint only queues that job, so a single call
      // returning isSuccess means "paid", not "granted" — announcing success on
      // it told the member their voucher was ready even when the job went on to
      // fail (out of stock, tier no longer eligible) and left them on an empty
      // My Vouchers. Same wait TopUp.vue does.
      const outcome = await MemberService.pollPaymentJobStatus(
        query.session_id,
        query.airwallex_intent_id
      );

      if (outcome.status === "failed") {
        // Clear the gateway params here too: the stash has already been
        // consumed, so a reload would re-run this against nothing and could end
        // up opening the success sheet with no voucher name in it.
        this.$router.replace({ path: this.$route.path, query: {} });
        this.snackbar = "danger";
        this.message = outcome.message;
        this.setTimeOutNotification();
        this.hideLoader();
        return;
      }

      this.$router.replace({ path: this.$route.path, query: {} });
      this.pendingRedeem = stashed;
      // Ran out of window rather than finished: the payment is taken and the
      // job is still coming, so the sheet says so instead of claiming the
      // voucher is already in hand.
      this.redeemStillProcessing = outcome.status === "pending";
      $(".btn-success-modal").click();
      this.hideLoader();
    },
    // Closing the success sheet is the natural second read: the queued job has
    // had the time the member spent reading it, so balance and availability are
    // current by the time the list is on screen again.
    // The verify call only QUEUES ProcessWebhookJob (onQueue 'minty_api_high'),
    // so even a list fetched right after it can still show the voucher as
    // available. Closing the sheet is the natural second read: the queue has
    // had the time the member spent reading it.
    refreshOnSuccessClose() {
      this.redeemStillProcessing = false;
      this.getList();
    },
    getDefaultPaymentMode() {
      MemberService.getDefaultPaymentMode().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.paymentMode =
              parseInt(response.data.data.default_payment_mode) || STRIPE;
          }
        },
        () => {}
      );
    },
    async getList() {
      this.showLoader();
      this.notAvailable = false;
      const dataForm = { search_keyword: this.searchKeyword };

      // Bundles are a separate module: a merchant can have the catalogue and
      // not the bundles, so its failure must not blank the whole screen.
      const [single, bundle] = await Promise.all([
        PromocodeService.getRewardCatalogue(dataForm).then(
          (response) => response,
          (error) => ({ error: error })
        ),
        PromocodeService.getVoucherBundles(dataForm).then(
          (response) => response,
          () => null
        ),
      ]);

      if (single.error) {
        // A 500 "Cannot access this API!" means the merchant is not entitled
        // to the reward-catalogue module — show an empty state, not a toast.
        if (single.error?.response?.status === 500) {
          this.notAvailable = true;
        } else {
          this.snackbar = "danger";
          this.message = single.error;
          this.setTimeOutNotification();
        }
      } else if (single.data.result.isSuccess) {
        this.singleVouchers = single.data.data.vouchers ?? [];
        this.merchantMember = single.data.data.merchant_member ?? {};
        this.currentMember = single.data.data.member ?? {};
        this.pointName = single.data.data.point_name ?? "Point";
        this.pointIcon = single.data.data.point_icon ?? "";
        this.hasCustomPointIcon = !!single.data.data.point_icon_custom;
      } else {
        this.snackbar = "danger";
        this.message = single.data.result.message;
        this.setTimeOutNotification();
      }

      if (bundle && bundle.data.result.isSuccess) {
        this.bundleVouchers = bundle.data.data.vouchers ?? [];
        // A merchant selling only bundles still has to fill these in.
        if (!this.currentMember.id) {
          this.currentMember = bundle.data.data.member ?? {};
        }
        if (!this.merchantMember.reward_type) {
          this.merchantMember = bundle.data.data.merchant_member ?? {};
        }
      } else {
        this.bundleVouchers = [];
      }

      this.pruneFilters();
      this.hideLoader();
    },
    // A search can remove the last voucher of a reward type; leaving it selected
    // would pin the list empty with no way back except clearing the search.
    // Voucher kinds are a fixed pair, so they never need pruning.
    pruneFilters() {
      const types = this.typeOptions.map((option) => option.value);
      this.selectedTypes = this.selectedTypes.filter((value) =>
        types.includes(value)
      );
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
};
</script>

<style scoped>
/* Shell, tokens and sheet head come from assets/css/moo-sheet.css. */
.container-custom {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.content-custom {
  flex: 1;
  overflow: auto;
}

/* ---- stat cards ---- */
/* Spacing only; the cards come from moo-sheet.css. */
.rc-balances {
  margin-bottom: 16px;
}

/* ---- search ---- */
.rc-search {
  --color: var(--sheet) !important;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 12px;
  margin-bottom: 12px !important;
}
/* SearchInput is an underline field; the box takes over the focus affordance. */
.rc-search :deep(input.nosubmit) {
  border-bottom: none;
  padding: 11px 12px 11px 40px;
  border-radius: 12px;
}
.rc-search :deep(input[type="search"]:focus) {
  border: none !important;
  border-bottom: none !important;
}
.rc-search:focus-within {
  border-color: var(--sheet);
}

/* ---- filters ---- */
.rc-filters {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
.rc-select {
  position: relative;
  flex: 1;
  min-width: 0;
}
.rc-filter {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 12px;
  padding: 9px 12px;
  font-size: 13px;
  color: #241a12;
  cursor: pointer;
}
.rc-filter--open {
  border-color: var(--sheet);
}
.rc-filter__left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
/* text-overflow needs a real element: on the flex container it never applies
   to the anonymous text item, which clips mid-word instead. */
.rc-filter__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rc-filter__icon {
  font-size: 16px;
  color: var(--sheet);
  flex-shrink: 0;
}
.rc-filter__chevron {
  font-size: 18px;
  color: #857372;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}
.rc-filter__chevron--open {
  transform: rotate(180deg);
}
.rc-select__menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 12px;
  padding: 6px;
  max-height: 260px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 12px 28px rgba(25, 28, 30, 0.16);
}
.rc-select__opt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 9px 10px;
  font-size: 13px;
  border-radius: 8px;
  color: #241a12;
  cursor: pointer;
}
.rc-select__opt-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rc-select__opt--active {
  background: var(--sheet-08);
  color: var(--sheet);
  font-weight: 700;
}
.rc-select__check {
  font-size: 16px;
  color: var(--sheet);
  flex-shrink: 0;
}

/* ---- cards ---- */
.rc-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rc-card {
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 16px;
  padding: 12px;
  display: flex;
  gap: 12px;
}
.rc-card--ineligible {
  opacity: 0.62;
}
.rc-card__icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: var(--sheet-08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  cursor: pointer;
}
.rc-card__icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.rc-card__icon .material-icons-outlined {
  font-size: 24px;
  color: var(--sheet);
}
.rc-card__body {
  flex: 1;
  min-width: 0;
}
.rc-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 9999px;
  margin-bottom: 4px;
}
.rc-badge--single {
  background: var(--sheet-08);
  color: #7a6a54;
}
.rc-badge--bundle {
  background: var(--sheet-12);
  color: var(--sheet);
}
.rc-card__name {
  font-size: 14px;
  font-weight: 700;
  color: #241a12;
  cursor: pointer;
  word-break: break-word;
}
.rc-card__valid,
.rc-card__count {
  font-size: 12px;
  color: #857372;
  margin-top: 2px;
}
.rc-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
}
.rc-cost {
  font-size: 13px;
  font-weight: 800;
  color: var(--sheet);
  white-space: nowrap;
}
.rc-get {
  border: none;
  border-radius: 9999px;
  background: var(--sheet);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  padding: 7px 18px;
  cursor: pointer;
  flex-shrink: 0;
}
.rc-get--ineligible {
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  color: #a9a19a;
  cursor: not-allowed;
}
.rc-tnc {
  margin-top: 8px;
  font-size: 12px;
}
.rc-tnc span {
  color: var(--sheet);
  text-decoration: underline;
  cursor: pointer;
}

/* ---- empty / guest ---- */
.rc-empty,
.rc-guest {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 44px 0;
  color: #857372;
  font-size: 13px;
  text-align: center;
}
.rc-empty .material-icons-outlined,
.rc-guest .material-icons-outlined {
  font-size: 30px;
  color: var(--sheet-border);
}
.rc-guest p {
  margin: 0;
}
.rc-link {
  color: var(--sheet);
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
}
/* ---- terms & conditions sheet ----
   Slide-in and positioning come from .moo-bottom-sheet in moo-sheet.css; this
   block is only what is specific to the terms content. */
.rc-terms {
  border: 0;
  border-radius: 24px 24px 0 0;
  padding: 0 20px calc(20px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -12px 30px rgba(25, 28, 30, 0.12);
}
/* Drag handle, same affordance the other two sheets draw. */
.rc-terms::before {
  content: "";
  display: block;
  width: 36px;
  height: 4px;
  margin: 8px auto 0;
  border-radius: 999px;
  background: var(--sheet-border);
}
.rc-terms__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 0 12px;
}
.rc-terms__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: var(--sheet-12);
  color: var(--sheet);
  font-size: 20px;
}
.rc-terms__title {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 800;
  color: #241a12;
}
.rc-terms__close {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: var(--sheet-inset);
  color: #6b6868;
  cursor: pointer;
}
.rc-terms__close .material-icons-round {
  font-size: 18px;
}
.rc-terms__body {
  /* Merchant terms run to any length; the sheet stays put and the text scrolls
     inside it rather than pushing the Close button off screen. */
  max-height: 46vh;
  overflow-y: auto;
  background: var(--sheet-bg);
  border: 1px solid var(--sheet-border);
  border-radius: 14px;
  padding: 14px;
  font-size: 13.5px;
  line-height: 1.65;
  color: #4a4342;
  /* terms_conditions arrives as merchant-typed text with bare newlines. */
  white-space: pre-line;
  overflow-wrap: anywhere;
}
/* v-html content is outside scoped-style reach without :deep(). */
.rc-terms__body :deep(p) {
  margin: 0 0 8px;
}
.rc-terms__body :deep(p:last-child) {
  margin-bottom: 0;
}
.rc-terms__body :deep(ul),
.rc-terms__body :deep(ol) {
  margin: 0 0 8px;
  padding-left: 18px;
}
.rc-terms__body :deep(a) {
  color: var(--sheet);
  font-weight: 600;
  text-decoration: underline;
}
.rc-terms__done {
  width: 100%;
  height: 48px;
  margin-top: 14px;
  border: 0;
  border-radius: 9999px;
  background: var(--sheet);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.14);
  cursor: pointer;
  transition: transform 0.1s ease;
}
.rc-terms__done:active {
  transform: scale(0.98);
}
</style>
